/**
 * Calendar dates as "YYYY-MM-DD" strings, worked out in UTC so the answer
 * never depends on the server's or the browser's time zone.
 */
export type ISODate = string;

const DAY = 86_400_000;
const pad = (n: number) => String(n).padStart(2, "0");

export const isoOf = (y: number, m: number, d: number): ISODate => `${y}-${pad(m)}-${pad(d)}`;
const split = (d: ISODate): [number, number, number] => {
  const [y, m, dd] = d.slice(0, 10).split("-").map(Number);
  return [y!, m!, dd!];
};
const toTime = (d: ISODate) => {
  const [y, m, dd] = split(d);
  return Date.UTC(y, m - 1, dd);
};
const fromTime = (t: number): ISODate => new Date(t).toISOString().slice(0, 10);

export const isISODate = (s: unknown): s is ISODate => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s) && fromTime(toTime(s)) === s;
export const addDays = (d: ISODate, n: number): ISODate => fromTime(toTime(d) + n * DAY);
export const daysBetween = (a: ISODate, b: ISODate) => Math.round((toTime(b) - toTime(a)) / DAY);
export const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** n months on, on `day` of the month (the date's own day unless given), clamped to the month's last day. */
export function addMonths(d: ISODate, n: number, day?: number): ISODate {
  const [y, m, dd] = split(d);
  const idx = y * 12 + (m - 1) + n;
  const ny = Math.floor(idx / 12);
  const nm = (idx % 12) + 1;
  return isoOf(ny, nm, Math.min(day ?? dd, daysInMonth(ny, nm)));
}

/** 0 = Sunday … 6 = Saturday. */
export const weekday = (d: ISODate) => new Date(toTime(d)).getUTCDay();
/** The Monday on or before the date. */
export const mondayOf = (d: ISODate) => addDays(d, -((weekday(d) + 6) % 7));
export const monthKey = (d: ISODate) => d.slice(0, 7);
export const dayOfMonth = (d: ISODate) => split(d)[2];
export function endOfMonth(d: ISODate): ISODate {
  const [y, m] = split(d);
  return isoOf(y, m, daysInMonth(y, m));
}
export const maxDate = (a: ISODate, b: ISODate) => (a > b ? a : b);
export const minDate = (a: ISODate, b: ISODate) => (a < b ? a : b);

/** The last day of the financial year containing `d`, for a year starting on the 1st of `startMonth` (7 = July, so the year ends 30 June). */
export function yearEndOf(d: ISODate, startMonth: number): ISODate {
  const [y, m] = split(d);
  const endYear = m >= startMonth ? (startMonth === 1 ? y : y + 1) : y;
  const endMonth = startMonth === 1 ? 12 : startMonth - 1;
  return isoOf(endYear, endMonth, daysInMonth(endYear, endMonth));
}

/** Month keys ("2026-10") from the month of `from` to the month of `to`, inclusive. */
export function monthsBetween(from: ISODate, to: ISODate): string[] {
  const out: string[] = [];
  for (let d = isoOf(...(split(from).slice(0, 2) as [number, number]), 1); monthKey(d) <= monthKey(to); d = addMonths(d, 1, 1)) out.push(monthKey(d));
  return out;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "2026-11" → "Nov 26". */
export const monthLabel = (key: string) => `${MONTHS[Number(key.slice(5, 7)) - 1]} ${key.slice(2, 4)}`;
/** "2026-11-23" → "23 Nov 2026". */
export function dayLabel(d: ISODate): string {
  const [y, m, dd] = split(d);
  return `${dd} ${MONTHS[m - 1]} ${y}`;
}

/** Today's date in London, wherever this runs. */
export const londonToday = (now = new Date()): ISODate => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
